import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getSuperAdminHeader } from './SuperAdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Wifi, WifiOff, AlertTriangle, CheckCircle2, RefreshCw, Server, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SuperAdminHealthPage() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (showToast) => {
    if (showToast) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/super/health`, { headers: getSuperAdminHeader() });
      setHealth(res.data);
      if (showToast) toast.success('Status atualizado');
    } catch (err) { console.error(err); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchData(false); }, []);

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;

  const s = health?.summary || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Monitoramento de Saúde</h2>
        <Button variant="outline" size="sm" onClick={() => fetchData(true)} disabled={refreshing}>
          {refreshing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}Atualizar
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold">{s.total || 0}</p>
            <p className="text-sm text-gray-500">Total Tenants</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-green-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{s.healthy || 0}</p>
            <p className="text-sm text-gray-500">Saudáveis</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-red-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{s.errors || 0}</p>
            <p className="text-sm text-gray-500">Com Erro</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-gray-400">{s.not_configured || 0}</p>
            <p className="text-sm text-gray-500">Não Configurado</p>
          </CardContent>
        </Card>
      </div>

      {/* Detail Table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">Status</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead>URL Zabbix</TableHead>
                <TableHead className="w-24 text-center">Hosts</TableHead>
                <TableHead className="w-24 text-center">Problemas</TableHead>
                <TableHead>Erro</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(health?.tenants || []).map(t => (
                <TableRow key={t.tenant_id}>
                  <TableCell>
                    {t.status === 'healthy' && <CheckCircle2 className="h-5 w-5 text-green-500" />}
                    {t.status === 'error' && <AlertTriangle className="h-5 w-5 text-red-500" />}
                    {t.status === 'not_configured' && <WifiOff className="h-5 w-5 text-gray-300" />}
                  </TableCell>
                  <TableCell className="font-medium">{t.company_name}</TableCell>
                  <TableCell className="text-sm text-gray-500 font-mono truncate max-w-[300px]">{t.zabbix_url || '-'}</TableCell>
                  <TableCell className="text-center font-semibold">{t.hosts || '-'}</TableCell>
                  <TableCell className="text-center">
                    {t.problems > 0 ? <Badge className="bg-red-500 text-white">{t.problems}</Badge> : <span className="text-gray-400">0</span>}
                  </TableCell>
                  <TableCell className="text-xs text-red-500 max-w-[200px] truncate">{t.error || ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
